import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Search } from "lucide-react";
import HandymanCard from "../components/HandymanCard";

export default function Browse() {
  const [sp, setSp] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [handymen, setHandymen] = useState([]);
  const [q, setQ] = useState(sp.get("q") || "");
  const [campus, setCampus] = useState(sp.get("campus") || "");
  const category = sp.get("category") || "";

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const t = setTimeout(() => {
      const params = {};
      if (category) params.category = category;
      if (campus) params.campus = campus;
      if (q) params.q = q;
      api.get("/handymen", { params, signal: controller.signal })
        .then((r) => setHandymen(r.data))
        .catch((e) => {
          if (e.name !== "CanceledError" && e.code !== "ERR_CANCELED") {
            // silent — non-cancel errors are edge cases only
          }
        });
    }, 250);
    return () => { clearTimeout(t); controller.abort(); };
  }, [category, campus, q]);

  const catMap = Object.fromEntries(categories.map((c) => [c.id, c.label]));

  const selectCategory = (id) => {
    const next = new URLSearchParams(sp);
    if (id) next.set("category", id); else next.delete("category");
    setSp(next);
  };

  return (
    <div className="bg-cream min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="font-display text-4xl lg:text-5xl text-charcoal">Browse Move-in Pros</h1>
        <p className="text-charcoal/70 mt-2">Vetted pros near your campus, ready for move-in day.</p>

        <div className="mt-8 flex flex-col lg:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute top-1/2 -translate-y-1/2 left-4 text-charcoal/40" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or keyword…"
              data-testid="search-input" className="pl-10 h-12 rounded-full bg-[#131C2E] border-[#1F2A40]" />
          </div>
          <Input value={campus} onChange={(e) => setCampus(e.target.value)} placeholder="Campus (e.g. NYU)"
            data-testid="campus-input" className="h-12 rounded-full bg-[#131C2E] border-[#1F2A40] lg:w-56" />
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          <Button variant={!category ? "default" : "outline"} onClick={() => selectCategory("")}
            data-testid="filter-all" className={`rounded-full ${!category ? "bg-terracotta hover:bg-terracotta/90" : "border-[#1F2A40]"}`}>
            All
          </Button>
          {categories.map((c) => (
            <Button key={c.id} variant={category === c.id ? "default" : "outline"}
              onClick={() => selectCategory(c.id)} data-testid={`filter-${c.id}`}
              className={`rounded-full ${category === c.id ? "bg-terracotta hover:bg-terracotta/90" : "border-[#1F2A40]"}`}>
              {c.label}
            </Button>
          ))}
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {handymen.map((h) => <HandymanCard key={h.user_id} h={h} categoryMap={catMap} />)}
        </div>
        {handymen.length === 0 && (
          <div className="mt-16 text-center text-charcoal/60" data-testid="no-results">
            No Move-in Pros match — try clearing a filter.
          </div>
        )}
      </div>
    </div>
  );
}
